/**
 * Service d'alertes unifiées (météo + événements locaux + calendrier)
 * Centralise la logique de génération, scoring et filtrage des alertes
 * pour les dashboards fixe, itinérant et la page recommandations.
 */
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of, map, catchError, switchMap } from 'rxjs';
import { LocationService } from './location.service';
import {
  EventWeatherForecast,
  HourlyWeatherSlot,
  NearbyEvent,
} from '../models/location.models';

// =====================================================
// Interface alerte unifiée
// =====================================================

export interface UnifiedAlert {
  source: 'weather' | 'event' | 'calendar';
  icon: string;
  title: string;
  detail: string;
  impactPct: number;
  confidence: number;
  date: string; // ISO YYYY-MM-DD
  dateLabel: string; // "Lundi 31 mars à Nice"
  expanded: boolean;
  hourlySlots?: HourlyWeatherSlot[];
  showHourly?: boolean;
}

// =====================================================
// Données calendaires françaises
// =====================================================

/**
 * Calcul de la date de Pâques — algorithme de Meeus (gregorien anonyme).
 * Retourne un Date pour l'année donnée.
 */
export function computeEasterSunday(year: number): Date {
  const n19 = year % 19;
  const cent = Math.floor(year / 100);
  const centRem = year % 100;
  const q1 = Math.floor(cent / 4);
  const r1 = cent % 4;
  const p = Math.floor((cent + 8) / 25);
  const q2 = Math.floor((cent - p + 1) / 3);
  const epact = (19 * n19 + cent - q1 - q2 + 15) % 30;
  const q3 = Math.floor(centRem / 4);
  const r2 = centRem % 4;
  const dayShift = (32 + 2 * r1 + 2 * q3 - epact - r2) % 7;
  const correction = Math.floor((n19 + 11 * epact + 22 * dayShift) / 451);
  const rawMonth = Math.floor((epact + dayShift - 7 * correction + 114) / 31);
  const rawDay = ((epact + dayShift - 7 * correction + 114) % 31) + 1;
  return new Date(year, rawMonth - 1, rawDay);
}

/** Tous les jours fériés français pour une année donnée. */
export function getFrenchPublicHolidays(
  year: number,
): Array<{ date: string; name: string }> {
  const pad2 = (v: number) => String(v).padStart(2, '0');
  const isoDate = (d: Date) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const easter = computeEasterSunday(year);
  const addDays = (base: Date, offset: number): Date => {
    const d = new Date(base);
    d.setDate(base.getDate() + offset);
    return d;
  };

  return [
    { date: `${year}-01-01`, name: "Jour de l'An" },
    { date: isoDate(addDays(easter, 1)), name: 'Lundi de Pâques' },
    { date: `${year}-05-01`, name: 'Fête du Travail' },
    { date: `${year}-05-08`, name: 'Victoire 1945' },
    { date: isoDate(addDays(easter, 39)), name: 'Ascension' },
    { date: isoDate(addDays(easter, 50)), name: 'Lundi de Pentecôte' },
    { date: `${year}-07-14`, name: 'Fête nationale' },
    { date: `${year}-08-15`, name: 'Assomption' },
    { date: `${year}-11-01`, name: 'Toussaint' },
    { date: `${year}-11-11`, name: 'Armistice' },
    { date: `${year}-12-25`, name: 'Noël' },
  ];
}

/** Vacances scolaires (toutes zones confondues, plage la plus large). */
export const SCHOOL_HOLIDAY_RANGES: ReadonlyArray<{
  name: string;
  start: string;
  end: string;
}> = [
  { name: 'Vacances de Toussaint', start: '2024-10-19', end: '2024-11-04' },
  { name: 'Vacances de Noël', start: '2024-12-21', end: '2025-01-06' },
  { name: "Vacances d'hiver", start: '2025-02-08', end: '2025-03-10' },
  { name: 'Vacances de printemps', start: '2025-04-05', end: '2025-05-05' },
  { name: "Vacances d'été", start: '2025-07-05', end: '2025-09-01' },
  { name: 'Vacances de Toussaint', start: '2025-10-18', end: '2025-11-03' },
  { name: 'Vacances de Noël', start: '2025-12-20', end: '2026-01-05' },
  { name: "Vacances d'hiver", start: '2026-02-07', end: '2026-03-09' },
  { name: 'Vacances de printemps', start: '2026-04-04', end: '2026-05-04' },
];

/** Périodes du Ramadan (approximations astronomiques). */
export const RAMADAN_RANGES: ReadonlyArray<{
  start: string;
  end: string;
}> = [
  { start: '2025-02-28', end: '2025-03-29' },
  { start: '2026-02-17', end: '2026-03-19' },
  { start: '2027-02-07', end: '2027-03-08' },
];

// =====================================================
// Service injectable (providedIn: root)
// =====================================================

@Injectable({ providedIn: 'root' })
export class UnifiedAlertService {
  private locationService = inject(LocationService);

  /**
   * Charge les alertes unifiées pour les N prochains jours.
   * Retourne un Observable de UnifiedAlert[] trié par impact, max `maxPerDay` par jour, `maxTotal` au total.
   * `excludeEventNames` permet d'exclure les événements que le marchand a déjà planifiés
   * (ex: marchand itinérant qui participe déjà à l'événement — pas besoin d'alerte d'affluence).
   */
  loadAlerts(
    lat: number,
    lng: number,
    city: string,
    options?: {
      days?: number;
      maxPerDay?: number;
      maxTotal?: number;
      excludeEventNames?: string[];
    },
  ): Observable<UnifiedAlert[]> {
    const days = options?.days ?? 2;
    const maxPerDay = options?.maxPerDay ?? 2;
    const maxTotal = options?.maxTotal ?? 4;
    const excludeNames = (options?.excludeEventNames ?? []).map((n) =>
      this.normalizeForMatch(n),
    );

    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const targetDates: string[] = [];
    for (let i = 0; i < days; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      targetDates.push(d.toISOString().split('T')[0]);
    }

    // 1. Alertes calendaires (synchrone, déterministe)
    const calendarAlerts: UnifiedAlert[] = [];
    for (const dateStr of targetDates) {
      calendarAlerts.push(...this.buildCalendarAlerts(dateStr, city));
    }

    // 2. Météo + Événements (asynchrone)
    const weatherReqs = targetDates.map((date) =>
      this.locationService
        .getWeatherForDate(lat, lng, date)
        .pipe(
          catchError(() => of({ available: false } as EventWeatherForecast)),
        ),
    );

    const eventsReq = this.locationService
      .getLocalEvents(lat, lng, 15)
      .pipe(
        catchError(() =>
          of({ success: false, events: [] as NearbyEvent[], total: 0 }),
        ),
      );

    return forkJoin([...weatherReqs, eventsReq]).pipe(
      switchMap((results: unknown[]) => {
        const collected: UnifiedAlert[] = [...calendarAlerts];

        // Alertes météo
        const forecasts = results.slice(
          0,
          targetDates.length,
        ) as EventWeatherForecast[];
        for (const forecast of forecasts) {
          if (!forecast.available) continue;
          const f = forecast as Extract<
            EventWeatherForecast,
            { available: true }
          >;
          collected.push(...this.buildWeatherAlerts(f, city, now));
        }

        // Alertes événements — top 1 par jour par impact
        // Exclure les événements déjà planifiés par le marchand (itinérant)
        const eventsRes = results[results.length - 1] as {
          success: boolean;
          events: NearbyEvent[];
        };
        if (eventsRes.success && eventsRes.events?.length > 0) {
          const filteredEvents =
            excludeNames.length > 0
              ? eventsRes.events.filter(
                  (e) =>
                    !excludeNames.some((ex) =>
                      this.normalizeForMatch(e.name).includes(ex),
                    ),
                )
              : eventsRes.events;
          for (const dateStr of targetDates) {
            const dayEvts = filteredEvents.filter((e) => {
              const ed = new Date(e.date).toISOString().split('T')[0];
              return ed === dateStr;
            });
            if (dayEvts.length > 0) {
              const rank: Record<string, number> = {
                high: 3,
                medium: 2,
                low: 1,
              };
              dayEvts.sort(
                (a, b) =>
                  (rank[b.impactLevel] || 0) - (rank[a.impactLevel] || 0),
              );
              collected.push(this.buildEventAlert(dayEvts[0], city));
            }
          }
        }

        // Fusion : top N par jour, max total
        const merged = this.rankAndSlice(
          collected,
          targetDates,
          maxPerDay,
          maxTotal,
        );

        // Créneaux horaires pour les alertes météo du jour
        const todayWeather = merged.filter(
          (a) => a.source === 'weather' && a.date === todayStr,
        );
        if (todayWeather.length > 0) {
          return this.locationService.getWeatherSlots(lat, lng, todayStr).pipe(
            map((slots) => {
              if (slots.available && slots.slots) {
                for (const alert of todayWeather) {
                  alert.hourlySlots = slots.slots!;
                }
              }
              return merged;
            }),
            catchError(() => of(merged)),
          );
        }

        return of(merged);
      }),
    );
  }

  // ========== Alertes météo ==========

  buildWeatherAlerts(
    f: Extract<EventWeatherForecast, { available: true }>,
    city: string,
    now: Date,
  ): UnifiedAlert[] {
    const out: UnifiedAlert[] = [];
    const dateObj = new Date(f.date + 'T12:00:00');
    const hoursAway = (dateObj.getTime() - now.getTime()) / (1000 * 60 * 60);
    const conf = hoursAway < 24 ? 95 : hoursAway < 48 ? 88 : 78;
    const label = this.formatDateCity(dateObj, city);

    if (f.precipitationSum > 5) {
      const pct = f.precipitationSum > 15 ? -25 : -15;
      out.push({
        source: 'weather',
        icon: '🌧️',
        title: `Pluie annoncée (${Math.round(f.precipitationSum)}mm)`,
        detail: `Précipitations de ${Math.round(f.precipitationSum)}mm prévues. Les jours de pluie réduisent la fréquentation extérieure de ${Math.abs(pct)}%. Confiance : ${conf}% (prévision à ${Math.round(hoursAway)}h).`,
        impactPct: pct,
        confidence: conf,
        date: f.date,
        dateLabel: label,
        expanded: false,
      });
    }

    if (f.temperatureMin < 5) {
      out.push({
        source: 'weather',
        icon: '❄️',
        title: `Froid prévu (${Math.round(f.temperatureMin)}°C min)`,
        detail: `Température minimale de ${Math.round(f.temperatureMin)}°C. Le froid réduit les déplacements et la fréquentation de 12%. Confiance : ${conf}%.`,
        impactPct: -12,
        confidence: conf,
        date: f.date,
        dateLabel: label,
        expanded: false,
      });
    }

    if (f.temperatureMax > 35) {
      out.push({
        source: 'weather',
        icon: '🌡️',
        title: `Forte chaleur (${Math.round(f.temperatureMax)}°C max)`,
        detail: `Température maximale de ${Math.round(f.temperatureMax)}°C. Forte demande en boissons et produits frais (+10%), mais baisse de fréquentation globale (-5%). Confiance : ${conf}%.`,
        impactPct: -5,
        confidence: conf,
        date: f.date,
        dateLabel: label,
        expanded: false,
      });
    }

    return out;
  }

  // ========== Alertes calendaires ==========

  buildCalendarAlerts(dateStr: string, city: string): UnifiedAlert[] {
    const out: UnifiedAlert[] = [];
    const dateObj = new Date(dateStr + 'T12:00:00');
    const label = this.formatDateCity(dateObj, city);
    const yr = dateObj.getFullYear();
    const mo = dateObj.getMonth() + 1;
    const dy = dateObj.getDate();

    // Jours fériés
    const holidays = getFrenchPublicHolidays(yr);
    const holiday = holidays.find((h) => h.date === dateStr);
    if (holiday) {
      out.push({
        source: 'calendar',
        icon: '🇫🇷',
        title: `Jour férié : ${holiday.name}`,
        detail: `${holiday.name} — jour férié national. Population en congé, augmentation du trafic piéton de 20% en moyenne pour les commerces alimentaires. Confiance : 100%.`,
        impactPct: 20,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    // Vacances scolaires
    const schoolHol = SCHOOL_HOLIDAY_RANGES.find(
      (h) => dateStr >= h.start && dateStr <= h.end,
    );
    if (schoolHol) {
      out.push({
        source: 'calendar',
        icon: '🏖️',
        title: schoolHol.name,
        detail: `Période de vacances scolaires (${schoolHol.name}). Afflux de familles et touristes. Impact estimé : +15% de fréquentation. Confiance : 100%.`,
        impactPct: 15,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    // Ramadan
    const ramadan = RAMADAN_RANGES.find(
      (r) => dateStr >= r.start && dateStr <= r.end,
    );
    if (ramadan) {
      out.push({
        source: 'calendar',
        icon: '🌙',
        title: 'Période du Ramadan',
        detail:
          'Période de Ramadan. Baisse de la fréquentation en journée (-10%), hausse en soirée après la rupture du jeûne. Adapter les horaires de service. Confiance : 100%.',
        impactPct: -10,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    // Période de Noël (20 déc – 2 jan)
    const isXmasPeriod = (mo === 12 && dy >= 20) || (mo === 1 && dy <= 2);
    if (isXmasPeriod && !holiday) {
      out.push({
        source: 'calendar',
        icon: '🎄',
        title: "Période des fêtes de fin d'année",
        detail:
          'Période de Noël et Nouvel An. Forte augmentation de la demande en produits alimentaires et festifs (+30%). Confiance : 100%.',
        impactPct: 30,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    // Week-end de Pâques (vendredi saint → lundi)
    const easter = computeEasterSunday(yr);
    const easterWindow = [-2, -1, 0, 1].map((off) => {
      const d = new Date(easter);
      d.setDate(easter.getDate() + off);
      return d.toISOString().split('T')[0];
    });
    if (easterWindow.includes(dateStr) && !holiday) {
      out.push({
        source: 'calendar',
        icon: '🐣',
        title: 'Week-end de Pâques',
        detail:
          'Week-end pascal. Forte demande en chocolats, pâtisseries et produits festifs. Impact estimé : +25%. Confiance : 100%.',
        impactPct: 25,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    // Haute saison estivale (15 juin – 15 sept)
    const isSummer =
      (mo === 6 && dy >= 15) || (mo >= 7 && mo <= 8) || (mo === 9 && dy <= 15);
    if (isSummer && !schoolHol) {
      out.push({
        source: 'calendar',
        icon: '☀️',
        title: 'Haute saison estivale',
        detail:
          'Période estivale. Augmentation du tourisme et de la fréquentation (+15%). Privilégier boissons, glaces et produits frais. Confiance : 100%.',
        impactPct: 15,
        confidence: 100,
        date: dateStr,
        dateLabel: label,
        expanded: false,
      });
    }

    return out;
  }

  // ========== Événement → Alerte ==========

  private buildEventAlert(event: NearbyEvent, city: string): UnifiedAlert {
    const dateObj = new Date(event.date);
    const impactMap: Record<string, number> = {
      high: 30,
      medium: 15,
      low: 5,
    };
    const pct = impactMap[event.impactLevel] || 10;
    const loc = event.location?.city || city;

    return {
      source: 'event',
      icon: event.icon || '🎉',
      title: event.name,
      detail: `Événement local : ${event.name}. ${event.location?.name ? 'Lieu : ' + event.location.name + '. ' : ''}Impact estimé : +${pct}% de trafic environnant. Confiance : 85% (événement confirmé${event.source ? ' par ' + event.source : ''}).`,
      impactPct: pct,
      confidence: 85,
      date: dateObj.toISOString().split('T')[0],
      dateLabel: `${this.capitalizeLocalDate(dateObj)}${loc ? ' à ' + loc : ''}`,
      expanded: false,
    };
  }

  // ========== Helpers internes ==========

  /**
   * Normalise un nom d'événement pour la comparaison floue :
   * minuscule, sans accents, sans ponctuation superflue.
   */
  private normalizeForMatch(name: string): string {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // accents
      .replace(/[^a-z0-9\s]/g, '') // ponctuation
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ========== Fusion & tri ==========

  rankAndSlice(
    alerts: UnifiedAlert[],
    dates: string[],
    maxPerDay: number,
    maxTotal: number,
  ): UnifiedAlert[] {
    const result: UnifiedAlert[] = [];
    for (const dateStr of dates) {
      const dayBucket = alerts.filter((a) => a.date === dateStr);
      dayBucket.sort((a, b) => Math.abs(b.impactPct) - Math.abs(a.impactPct));
      result.push(...dayBucket.slice(0, maxPerDay));
      if (result.length >= maxTotal) break;
    }
    return result.slice(0, maxTotal);
  }

  // ========== Helpers UI ==========

  capitalizeLocalDate(dateObj: Date): string {
    const raw = dateObj.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }

  private formatDateCity(dateObj: Date, city: string): string {
    const dl = this.capitalizeLocalDate(dateObj);
    return city ? `${dl} à ${city}` : dl;
  }

  getConfidenceClass(confidence: number): string {
    if (confidence >= 90)
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    if (confidence >= 80)
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }

  getAlertClasses(alert: UnifiedAlert): Record<string, boolean> {
    return {
      'bg-amber-50 dark:bg-amber-900/20 border-amber-400 dark:border-amber-700':
        alert.source === 'weather',
      'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-700':
        alert.source === 'event',
      'bg-purple-50 dark:bg-purple-900/20 border-purple-400 dark:border-purple-700':
        alert.source === 'calendar',
    };
  }

  getAlertTitleClass(alert: UnifiedAlert): string {
    const map: Record<string, string> = {
      weather: 'text-amber-900 dark:text-amber-200',
      event: 'text-green-900 dark:text-green-200',
      calendar: 'text-purple-900 dark:text-purple-200',
    };
    return map[alert.source];
  }

  getImpactBadgeClass(impactPct: number): Record<string, boolean> {
    return {
      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300':
        impactPct > 0,
      'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300':
        impactPct < -15,
      'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300':
        impactPct >= -15 && impactPct <= 0,
    };
  }

  getAlertLinkClass(alert: UnifiedAlert): string {
    const map: Record<string, string> = {
      weather:
        'text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300',
      event:
        'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300',
      calendar:
        'text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300',
    };
    return map[alert.source];
  }

  getAlertDetailClass(alert: UnifiedAlert): string {
    const map: Record<string, string> = {
      weather: 'text-amber-700 dark:text-amber-300',
      event: 'text-green-700 dark:text-green-300',
      calendar: 'text-purple-700 dark:text-purple-300',
    };
    return map[alert.source];
  }

  getAlertDateBadgeClass(alert: UnifiedAlert): string {
    const map: Record<string, string> = {
      weather:
        'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
      event:
        'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30',
      calendar:
        'text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30',
    };
    return map[alert.source];
  }
}
